import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LessonWrapper from '../components/LessonWrapper';

const CodingLesson6Content = () => {
  const navigate = useNavigate();
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [userMessage, setUserMessage] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lessonContent, setLessonContent] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [hasStartedLesson, setHasStartedLesson] = useState(false);
  const [canInterrupt, setCanInterrupt] = useState(false);
  
  const recognition = useRef(null);
  const audioRef = useRef(null);
  const hasAutoStarted = useRef(false);
  const audioInitialized = useRef(false);
  const currentRequestController = useRef(null);

  // Auto-show lesson introduction (text only - no audio due to browser restrictions)
  useEffect(() => {
    if (lessonContent && !hasAutoStarted.current && !isLoadingContent) {
      hasAutoStarted.current = true;
      // Small delay to let page settle
      setTimeout(() => {
        showLessonIntroduction();
      }, 1500);
    }
  }, [lessonContent, isLoadingContent]);

  const showLessonIntroduction = () => {
    if (hasStartedLesson) return;
    
    setHasStartedLesson(true);

    // Build introduction message - simple ready state
    const introMessage = `Welcome! I'm Geni, your AI teacher! 🎓

Click the "▶️ Start Teaching" button below to begin Coding Lesson 6, or ask me any question!`;

    // Add Geni's greeting to conversation
    const greetingHistory = [{ role: 'assistant', content: introMessage }];
    setConversationHistory(greetingHistory);
  };

  const stopSpeaking = () => {
    // Stop audio playback
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsSpeaking(false);
    setCanInterrupt(false);
    
    // Cancel any ongoing API requests
    if (currentRequestController.current) {
      currentRequestController.current.abort();
      currentRequestController.current = null;
    }
    setIsProcessing(false);
    
    console.log('Geni interrupted - ready for questions!');
  };

  const startTeaching = async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    setIsSpeaking(true); // Show "Geni is teaching..." status immediately
    setCanInterrupt(true);

    try {
      // Start teaching Section 1 directly (no extra intro - already in conversation)
      await explainCurrentSection();

    } catch (error) {
      console.error('Failed to start teaching:', error);
      setIsProcessing(false);
      setCanInterrupt(false);
    }
  };

  const explainSpecificSection = async (sectionIndex) => {
    if (!lessonContent || !lessonContent.sections || !lessonContent.sections[sectionIndex]) {
      setIsProcessing(false);
      setCanInterrupt(false);
      return;
    }

    const section = lessonContent.sections[sectionIndex];
    console.log(`📚 Teaching Section ${sectionIndex + 1}: ${section.title}`);
    console.log('🔍 DEBUG: Section has teaching_script:', !!section.teaching_script);
    console.log('🔍 DEBUG: Section object keys:', Object.keys(section));
    
    setIsProcessing(true);
    setIsSpeaking(true); // Show "Geni is teaching..." status immediately
    setCanInterrupt(true);

    try {
      // 🚀 PERFORMANCE OPTIMIZATION: Use pre-generated teaching script
      // This loads in 0.05 seconds instead of 60+ seconds!
      if (section.teaching_script) {
        console.log('⚡ Using pre-generated teaching script (INSTANT loading)');
        let teachingScript = section.teaching_script;
        
        // Add question at the end of section
        teachingScript += '\n\n🤔 That completes this section! Do you have any questions about what we just learned? Feel free to ask me anything!';
        
        // Add to conversation history
        setConversationHistory(prev => [...prev, { role: 'assistant', content: teachingScript }]);
        
        console.log(`✅ Script loaded instantly (${teachingScript.length} chars)`);
        
        // Speak the script
        await speakText(teachingScript);
        
        setIsProcessing(false);
        setCanInterrupt(false);
        currentRequestController.current = null;
        return;
      }
      
      // FALLBACK: If no teaching script, use the old method (slower)
      console.log('⚠️ No pre-generated script found, using live generation (slower)...');
      
      // Pass FULL section content as context
      let fullContext = `Coding Lesson 6: ${lessonContent.title}\n\n`;
      
      // Add story if this is the first section
      if (sectionIndex === 0 && lessonContent.story) {
        fullContext += `STORY:\n${lessonContent.story}\n\n`;
      }
      
      fullContext += `SECTION ${sectionIndex + 1}: ${section.title}\n\nFULL CONTENT:\n${section.content}`;

      // Teaching instruction - teach complete section efficiently
      let teachingInstruction;
      if (sectionIndex === 0 && lessonContent.story) {
        teachingInstruction = `Teach Section 1 completely. Start: "Hello! I'm Geni, your Coding teacher! Today we're starting Coding Lesson 6: Talking Machines (Chatbots)!" Then teach EVERYTHING in order - tell the story, explain all concepts, do all activities, and cover all examples. Teach ALL content from this section in a natural, conversational way. Keep your language efficient but cover everything. DO NOT STOP until the section is complete.`;
      } else {
        teachingInstruction = `Teach "${section.title}" completely using ALL the content provided. Cover all concepts, examples, and activities. Be conversational and efficient. Don't skip anything - students are counting on getting the full lesson.`;
      }

      // Create abort controller for this request
      currentRequestController.current = new AbortController();

      // Use regular chat API (fallback)
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/avatar/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: teachingInstruction,
          conversation_history: conversationHistory.slice(-6),
          lesson_context: fullContext
        }),
        signal: currentRequestController.current.signal
      });

      if (!response.ok) throw new Error('Failed to get explanation');

      const data = await response.json();
      let aiResponse = data.response;
      
      // Add question at the end of section
      aiResponse += '\n\n🤔 That completes this section! Do you have any questions about what we just learned? Feel free to ask me anything!';

      // Add explanation to conversation
      setConversationHistory(prev => [...prev, { role: 'assistant', content: aiResponse }]);

      console.log(`✅ Received response (${aiResponse.length} chars)`);

      // Speak the explanation
      await speakText(aiResponse);

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Request aborted - interrupted by user');
      } else {
        console.error('Failed to explain section:', error);
      }
    } finally {
      setIsProcessing(false);
      setCanInterrupt(false);
      currentRequestController.current = null;
    }
  };

  const explainCurrentSection = async () => {
    // Use the specific section function with current section index
    await explainSpecificSection(currentSection);
  };


  useEffect(() => {
    // Fetch Lesson 6 Coding course content
    const fetchLessonContent = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/demo/coding/week/6`);
        if (response.ok) {
          const data = await response.json();
          setLessonContent(data);
        }
      } catch (error) {
        console.error('Failed to fetch lesson content:', error);
      } finally {
        setIsLoadingContent(false);
      }
    };

    fetchLessonContent();

    // Initialize speech recognition
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognition.current = new SpeechRecognition();
      recognition.current.continuous = false;
      recognition.current.interimResults = false;
      recognition.current.lang = 'en-US';

      recognition.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setUserMessage(transcript);
        handleSendMessage(transcript);
      };

      recognition.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'no-speech') {
          alert('No speech detected. Please try again!');
        }
      };

      recognition.current.onend = () => {
        setIsListening(false);
      };
    }
  }, []);

  const startListening = () => {
    if (recognition.current) {
      setIsListening(true);
      setUserMessage('');
      recognition.current.start();
    } else {
      alert('Speech recognition is not supported in your browser. Please type your message instead.');
    }
  };

  const stopListening = () => {
    if (recognition.current && isListening) {
      recognition.current.stop();
    }
  };

  const handleSendMessage = async (message) => {
    if (!message.trim()) return;
    
    // Allow interruption - stop current speech
    if (isSpeaking || isProcessing) {
      stopSpeaking();
      // Small delay to ensure clean state
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setIsProcessing(true);
    setIsSpeaking(true); // Show "Geni is teaching..." status immediately
    setCanInterrupt(true);

    // Add user message to history
    const newHistory = [...conversationHistory, { role: 'user', content: message }];
    setConversationHistory(newHistory);

    try {
      // Build lesson context if content is loaded
      let lessonContext = '';
      if (lessonContent && lessonContent.sections && lessonContent.sections[currentSection]) {
        const section = lessonContent.sections[currentSection];
        lessonContext = `Coding Lesson 6: ${lessonContent.title}\nCurrent Section: ${section.title}\n${section.content}`;
      }

      // Create abort controller
      currentRequestController.current = new AbortController();

      // Get AI response
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/avatar/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: message,
          conversation_history: newHistory.slice(-6), // Last 3 exchanges
          lesson_context: lessonContext
        }),
        signal: currentRequestController.current.signal
      });

      if (!response.ok) throw new Error('Chat failed');

      const data = await response.json();
      const aiResponse = data.response;

      // Add AI response to history
      setConversationHistory([...newHistory, { role: 'assistant', content: aiResponse }]);

      // Speak the response
      await speakText(aiResponse);

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Request aborted');
      } else {
        console.error('Chat error:', error);
        alert('Failed to get response. Please try again!');
      }
    } finally {
      setIsProcessing(false);
      setCanInterrupt(false);
      setUserMessage('');
      currentRequestController.current = null;
    }
  };

  const speakText = async (text) => {
    setIsGeneratingAudio(true); // Show "preparing..." message
    const startTime = Date.now();
    console.log("🎤 Generating speech audio... (this may take 30-60 seconds for long content)");

    try {
      // Initialize audio element on first use
      if (!audioInitialized.current && audioRef.current) {
        // Create a silent audio context to enable playback
        audioRef.current.volume = 1.0;
        audioInitialized.current = true;
      }

      // Limit text to 4000 characters for TTS (OpenAI limit is 4096)
      let textToSpeak = text;
      if (text.length > 4000) {
        console.warn(`Text too long for TTS (${text.length} chars), truncating to 4000`);
        textToSpeak = text.substring(0, 4000) + '...';
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/avatar/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: textToSpeak, voice: 'alloy' })
      });

      const endTime = Date.now();
      console.log(`✅ Audio generated in ${((endTime - startTime) / 1000).toFixed(1)} seconds`);
      
      setIsGeneratingAudio(false); // Audio ready
      setIsSpeaking(true); // Now actually speaking

      if (response.ok) {
        const audioBlob = await response.blob();
        const audioUrl = URL.createObjectURL(audioBlob);

        if (audioRef.current) {
          audioRef.current.src = audioUrl;
          
          // Try to play, catch if autoplay is blocked
          try {
            await audioRef.current.play();
            console.log('Audio playing successfully');
          } catch (playError) {
            console.error('Audio play error:', playError);
            alert('Please click anywhere on the page to enable audio. Then try again!');
            setIsSpeaking(false);
          }
        }
      } else {
        console.error('TTS request failed:', response.status);
        setIsGeneratingAudio(false);
        setIsSpeaking(false);
      }
    } catch (error) {
      console.error('Speech error:', error);
      alert('Voice generation failed. Please try again!');
      setIsGeneratingAudio(false);
      setIsSpeaking(false);
    }
  };

  const handleAudioEnd = () => {
    setIsSpeaking(false);
    setIsGeneratingAudio(false);
  };

  // Initialize audio on first click anywhere
  const handlePageClick = () => {
    if (!audioInitialized.current && audioRef.current) {
      // Create audio context on user interaction
      const initAudio = async () => {
        try {
          audioRef.current.volume = 1.0;
          audioInitialized.current = true;
          console.log('Audio initialized');
        } catch (e) {
          console.error('Audio init error:', e);
        }
      };
      initAudio();
    }
  };

  return (
    <div 
      onClick={handlePageClick}
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0a1628 0%, #1a2f4a 100%)',
        padding: '20px',
        position: 'relative',
        overflow: 'hidden'
      }}>
      {/* Animated particles */}
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: '4px',
            height: '4px',
            background: i % 2 === 0 ? '#ffd700' : '#00d9ff',
            borderRadius: '50%',
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animation: `twinkle ${2 + Math.random() * 3}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 2}s`,
            opacity: 0
          }}
        />
      ))}

      <div style={{
        maxWidth: '1600px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Audio Tip Banner */}
        {!audioInitialized.current && (
          <div style={{
            background: 'linear-gradient(135deg, #ffd700 0%, #ffa500 100%)',
            color: '#1a2f4a',
            padding: '12px 20px',
            borderRadius: '12px',
            marginBottom: '20px',
            textAlign: 'center',
            fontSize: '16px',
            fontWeight: '600',
            boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)'
          }}>
            🔊 Tip: Make sure your sound is ON! Click the "Start Teaching" button to hear Geni's voice.
          </div>
        )}

        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255,255,255,0.2)',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            ← Back
          </button>

          <div style={{ textAlign: 'center' }}>
            <h1 style={{
              fontSize: '36px',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '8px',
              textShadow: '0 0 20px rgba(0,217,255,0.5)'
            }}>
              🎓 Talk with Geni - Your AI Teacher
            </h1>
            <p style={{
              fontSize: '18px',
              color: 'rgba(255,255,255,0.9)'
            }}>
              Coding Lesson 6: Talking Machines (Chatbots)
            </p>
          </div>

          <div style={{ width: '120px' }}></div>
        </div>

        {/* Main 2-Column Layout */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '30px',
          alignItems: 'start'
        }}>
          {/* LEFT COLUMN - Geni Teacher & Interaction */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
            {/* Geni Video Card */}
            <div style={{
              background: 'rgba(255,255,255,0.05)',
              backdropFilter: 'blur(20px)',
              border: '2px solid rgba(255,255,255,0.1)',
              borderRadius: '20px',
              padding: '40px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              textAlign: 'center'
            }}>
              {/* Animated Geni Video */}
              <div style={{
                position: 'relative',
                width: '100%',
                height: '400px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '30px',
                overflow: 'visible'
              }}>
                {/* Animated Floating Fun Elements for Kids */}
                <div style={{ position: 'absolute', top: '5%', left: '3%', animation: 'floatKids 3s ease-in-out infinite', fontSize: '42px', zIndex: 1 }}>🤖</div>
                <div style={{ position: 'absolute', top: '15%', right: '5%', animation: 'floatKids 4s ease-in-out infinite 0.5s', fontSize: '38px', zIndex: 1 }}>⭐</div>
                <div style={{ position: 'absolute', bottom: '12%', left: '8%', animation: 'floatKids 3.5s ease-in-out infinite 1s', fontSize: '40px', zIndex: 1 }}>🎓</div>
                <div style={{ position: 'absolute', bottom: '22%', right: '4%', animation: 'floatKids 4.5s ease-in-out infinite 1.5s', fontSize: '35px', zIndex: 1 }}>💡</div>
                <div style={{ position: 'absolute', top: '32%', left: '1%', animation: 'floatKids 3.8s ease-in-out infinite 0.8s', fontSize: '33px', zIndex: 1 }}>🚀</div>
                <div style={{ position: 'absolute', top: '48%', right: '2%', animation: 'floatKids 4.2s ease-in-out infinite 2s', fontSize: '38px', zIndex: 1 }}>🎨</div>
                <div style={{ position: 'absolute', top: '68%', left: '6%', animation: 'floatKids 3.3s ease-in-out infinite 0.3s', fontSize: '36px', zIndex: 1 }}>🎮</div>
                <div style={{ position: 'absolute', bottom: '8%', right: '10%', animation: 'floatKids 4s ease-in-out infinite 1.2s', fontSize: '42px', zIndex: 1 }}>🌟</div>
                <div style={{ position: 'absolute', top: '25%', left: '12%', animation: 'floatKids 3.6s ease-in-out infinite 0.6s', fontSize: '30px', zIndex: 1 }}>✨</div>
                <div style={{ position: 'absolute', bottom: '35%', right: '8%', animation: 'floatKids 4.3s ease-in-out infinite 1.8s', fontSize: '34px', zIndex: 1 }}>🎯</div>
                
                {/* Glow effect */}
                {(isSpeaking || isListening) && (
                  <div style={{
                    position: 'absolute',
                    inset: '-30px',
                    background: `radial-gradient(circle, ${isListening ? 'rgba(255,215,0,0.3)' : 'rgba(0,217,255,0.3)'} 0%, transparent 70%)`,
                    borderRadius: '50%',
                    animation: 'pulse 2s ease-in-out infinite',
                    filter: 'blur(30px)'
                  }}></div>
                )}

                {/* Geni Animated Video */}
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    filter: (isSpeaking || isListening) ? 'drop-shadow(0 0 30px rgba(0,217,255,0.8))' : 'drop-shadow(0 10px 20px rgba(0,0,0,0.5))',
                    animation: 'floatGenie 3s ease-in-out infinite',
                    position: 'relative',
                    zIndex: 2
                  }}
                >
                  <source src="https://customer-assets.emergentagent.com/job_genie-learn/artifacts/pfidtz26_video.mp4" type="video/mp4" />
                </video>

                {/* Magical particles */}
                {(isSpeaking || isListening) && (
                  <>
                    {[...Array(8)].map((_, i) => (
                      <div
                        key={i}
                        style={{
                          position: 'absolute',
                          width: '8px',
                          height: '8px',
                          background: i % 2 === 0 ? '#ffd700' : '#00d9ff',
                          borderRadius: '50%',
                          top: '50%',
                          left: '50%',
                          animation: `orbit${i} 3s linear infinite`,
                          boxShadow: `0 0 10px ${i % 2 === 0 ? '#ffd700' : '#00d9ff'}`
                        }}
                      />
                    ))}
                  </>
                )}
              </div>

              {/* Status */}
              <div style={{
                textAlign: 'center',
                fontSize: '20px',
                fontWeight: '600',
                color: 'white',
                marginBottom: '25px',
                minHeight: '30px'
              }}>
                {isListening && '🎤 Listening...'}
                {isGeneratingAudio && '⏳ Geni is preparing to teach... (generating audio, please wait ~30-60 seconds)'}
                {isSpeaking && canInterrupt && !isGeneratingAudio && '💬 Geni is teaching... (You can interrupt!)'}
                {isSpeaking && !canInterrupt && !isGeneratingAudio && '💬 Geni is speaking...'}
                {isProcessing && !isSpeaking && !isGeneratingAudio && '🤔 Thinking...'}
                {!isListening && !isSpeaking && !isProcessing && hasStartedLesson && conversationHistory.length === 1 && '🎓 Ready to teach!'}
                {!isListening && !isSpeaking && !isProcessing && (!hasStartedLesson || conversationHistory.length > 1) && '👂 Ask me anything!'}
              </div>

              {/* Start Teaching Button */}
              {hasStartedLesson && conversationHistory.length === 1 && !isProcessing && (
                <button
                  onClick={startTeaching}
                  style={{
                    width: '100%',
                    padding: '25px',
                    background: 'linear-gradient(135deg, #ffd700 0%, #ffa500 100%)',
                    color: '#1a2f4a',
                    border: 'none',
                    borderRadius: '16px',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    boxShadow: '0 8px 20px rgba(255,215,0,0.4)',
                    marginBottom: '15px'
                  }}
                >
                  ▶️ Start Teaching
                </button>
              )}

              {/* Stop/Interrupt Button - Shows when Geni is speaking */}
              {canInterrupt && (isSpeaking || isProcessing) && (
                <button
                  onClick={stopSpeaking}
                  style={{
                    width: '100%',
                    padding: '20px',
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    boxShadow: '0 8px 20px rgba(239,68,68,0.4)',
                    marginBottom: '15px',
                    animation: 'pulse 2s ease-in-out infinite'
                  }}
                >
                  ✋ Stop Geni (Ask Question)
                </button>
              )}

              {/* Voice Input Button */}
              <button
                onClick={startListening}
                disabled={isListening || isProcessing}
                style={{
                  width: '100%',
                  padding: '20px',
                  background: (isListening || isProcessing)
                    ? 'rgba(203, 213, 224, 0.3)'
                    : 'linear-gradient(135deg, #00d9ff 0%, #0099cc 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  cursor: (isListening || isProcessing) ? 'not-allowed' : 'pointer',
                  boxShadow: isListening ? 'none' : '0 8px 20px rgba(0,217,255,0.4)',
                  marginBottom: '12px',
                  opacity: (isListening || isProcessing) ? 0.6 : 1
                }}
              >
                {isListening ? '🎙️ Listening...' : '🎤 Click to Speak'}
              </button>

              {/* Text Input */}
              <div style={{ marginTop: '20px' }}>
                <input
                  type="text"
                  value={userMessage}
                  onChange={(e) => setUserMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && userMessage.trim()) {
                      handleSendMessage(userMessage);
                    }
                  }}
                  placeholder="Or type your question..."
                  disabled={isProcessing}
                  style={{
                    width: '100%',
                    padding: '15px',
                    background: 'rgba(255,255,255,0.1)',
                    border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '12px',
                    color: 'white',
                    fontSize: '16px',
                    marginBottom: '12px',
                    outline: 'none'
                  }}
                />
                <button
                  onClick={() => handleSendMessage(userMessage)}
                  disabled={!userMessage.trim() || isProcessing}
                  style={{
                    width: '100%',
                    padding: '15px',
                    background: (!userMessage.trim() || isProcessing)
                      ? 'rgba(203, 213, 224, 0.3)'
                      : 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '18px',
                    fontWeight: '600',
                    cursor: (!userMessage.trim() || isProcessing) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isProcessing ? 'Processing...' : 'Send Message'}
                </button>
              </div>
            </div>

            {/* Conversation Card */}
            <div style={{
              background: 'rgba(255,255,255,0.05)',
              backdropFilter: 'blur(20px)',
              border: '2px solid rgba(255,255,255,0.1)',
              borderRadius: '20px',
              padding: '30px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              maxHeight: '400px',
              overflowY: 'auto'
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '20px',
                borderBottom: '3px solid #00d9ff',
                paddingBottom: '10px'
              }}>
                💬 Conversation
              </h2>

              {/* Conversation History */}
              <div>
                {conversationHistory.map((msg, index) => (
                  <div
                    key={index}
                    style={{
                      background: msg.role === 'user' ? 'rgba(255,255,255,0.1)' : 'rgba(0,217,255,0.1)',
                      padding: '12px 16px',
                      borderRadius: '12px',
                      marginBottom: '12px',
                      borderLeft: `4px solid ${msg.role === 'user' ? '#ffd700' : '#00d9ff'}`
                    }}
                  >
                    <div style={{
                      fontSize: '12px',
                      color: msg.role === 'user' ? '#ffd700' : '#00d9ff',
                      marginBottom: '4px',
                      fontWeight: '600'
                    }}>
                      {msg.role === 'user' ? 'You:' : 'Geni:'}
                    </div>
                    <div style={{ color: 'white', fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                      {msg.content}
                    </div>
                  </div>
                ))}

                {conversationHistory.length === 0 && !hasStartedLesson && (
                  <div style={{
                    textAlign: 'center',
                    color: 'rgba(255,255,255,0.5)',
                    padding: '40px 20px',
                    fontSize: '16px'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎓</div>
                    Geni is preparing your lesson...
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN - Lesson Content & Controls */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
            {/* Lesson Content Card */}
            <div style={{
              background: 'rgba(255,255,255,0.05)',
              backdropFilter: 'blur(20px)',
              border: '2px solid rgba(255,255,255,0.1)',
              borderRadius: '20px',
              padding: '30px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              maxHeight: '85vh',
              overflowY: 'auto'
            }}>
              <h2 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '20px',
                borderBottom: '3px solid #ffd700',
                paddingBottom: '12px',
                textAlign: 'center'
              }}>
                📚 Lesson Content
              </h2>

              {isLoadingContent ? (
                <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', padding: '40px' }}>
                  Loading lesson...
                </div>
              ) : lessonContent ? (
                <>
                  {/* MOVED TO TOP: Section Navigation & Teaching Controls */}
                  {lessonContent.sections && lessonContent.sections.length > 0 && (
                    <div style={{
                      background: 'rgba(0,217,255,0.05)',
                      border: '2px solid rgba(0,217,255,0.2)',
                      borderRadius: '16px',
                      padding: '20px',
                      marginBottom: '25px'
                    }}>
                      {/* Section Navigation Buttons */}
                      <h3 style={{
                        color: '#00d9ff',
                        fontSize: '18px',
                        marginBottom: '15px',
                        textAlign: 'center',
                        fontWeight: '600'
                      }}>
                        📖 Choose a Section:
                      </h3>
                      <div style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '20px',
                        flexWrap: 'wrap',
                        justifyContent: 'center'
                      }}>
                        {lessonContent.sections.map((section, idx) => (
                          <button
                            key={idx}
                            onClick={async () => {
                              console.log(`🔘 Section button ${idx + 1} clicked`);
                              setCurrentSection(idx);
                              // Wait for state to update, then teach
                              if (hasStartedLesson) {
                                // Use a callback to ensure we teach the correct section
                                setTimeout(() => {
                                  console.log(`📚 Will teach section ${idx + 1}`);
                                  explainSpecificSection(idx);
                                }, 100);
                              }
                            }}
                            style={{
                              padding: '14px 22px',
                              background: currentSection === idx
                                ? 'linear-gradient(135deg, #00d9ff 0%, #0099cc 100%)'
                                : 'rgba(255,255,255,0.1)',
                              color: 'white',
                              border: '2px solid rgba(255,255,255,0.2)',
                              borderRadius: '12px',
                              fontSize: '15px',
                              fontWeight: '700',
                              cursor: 'pointer',
                              boxShadow: currentSection === idx ? '0 4px 12px rgba(0,217,255,0.5)' : 'none',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            {idx + 1}. {section.title}
                          </button>
                        ))}
                      </div>

                      {/* PDF Download Button */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginBottom: '20px'
                      }}>
                        <a
                          href="https://customer-assets.emergentagent.com/job_genielearn/artifacts/bugvxk5b_Kids%20Genie%20Coding%20Lesson%206.pdf"
                          download="Kids Genie Coding Lesson 6 Student Book.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            padding: '14px 28px',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: 'white',
                            border: '2px solid rgba(255,255,255,0.3)',
                            borderRadius: '12px',
                            fontSize: '16px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            boxShadow: '0 4px 12px rgba(16,185,129,0.4)',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          📥 Download Coding Lesson 6 Student Book (PDF)
                        </a>
                      </div>

                      {/* Finish Lesson Button - Shows when on last section */}
                      {currentSection === 3 && (
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '15px',
                          marginTop: '30px',
                          padding: '25px',
                          background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                          borderRadius: '15px',
                          border: '3px solid rgba(255,255,255,0.3)'
                        }}>
                          <p style={{ color: 'white', fontSize: '18px', fontWeight: '700', margin: 0 }}>
                            🎉 You've completed all sections of Lesson 6!
                          </p>
                          <button
                            onClick={async () => {
                              try {
                                // Mark Coding Lesson 6 as complete
                                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lessons/week-6-coding/complete`, {
                                  method: 'POST',
                                  credentials: 'include'
                                });

      const endTime = Date.now();
      console.log(`✅ Audio generated in ${((endTime - startTime) / 1000).toFixed(1)} seconds`);
      
      setIsGeneratingAudio(false); // Audio ready
      setIsSpeaking(true); // Now actually speaking

      if (response.ok) {
                                  alert('🎉 Congratulations! Coding Lesson 6 is complete! Lesson 7 will unlock in 4 days. You can review this lesson anytime during the waiting period.');
                                  window.location.href = '/dashboard';
                                } else {
                                  alert('Please login to save your progress');
                                }
                              } catch (error) {
                                console.error('Error completing lesson:', error);
                                alert('Error saving progress. Please try again.');
                              }
                            }}
                            style={{
                              padding: '16px 40px',
                              background: 'white',
                              color: '#f59e0b',
                              border: 'none',
                              borderRadius: '12px',
                              fontSize: '18px',
                              fontWeight: '700',
                              cursor: 'pointer',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                            }}
                          >
                            ✅ Finish Coding Lesson 6 & Unlock Lesson 7 (4 days)
                          </button>
                        </div>
                      )}

                      {/* Teaching Control Buttons */}
                      {hasStartedLesson && (
                        <>
                          <div style={{
                            borderTop: '1px solid rgba(255,255,255,0.1)',
                            paddingTop: '20px',
                            marginTop: '5px'
                          }}>
                            <h3 style={{
                              color: '#ffd700',
                              fontSize: '18px',
                              marginBottom: '15px',
                              textAlign: 'center',
                              fontWeight: '600'
                            }}>
                              🎓 Teaching Controls:
                            </h3>
                            <div style={{
                              display: 'flex',
                              gap: '10px',
                              justifyContent: 'center',
                              flexWrap: 'wrap'
                            }}>
                              {currentSection < lessonContent.sections.length - 1 && (
                                <button
                                  onClick={() => {
                                    const nextSection = currentSection + 1;
                                    console.log(`⏭️ Next Section button: moving to section ${nextSection + 1}`);
                                    setCurrentSection(nextSection);
                                    // Teach the next section directly
                                    setTimeout(() => explainSpecificSection(nextSection), 100);
                                  }}
                                  disabled={isProcessing}
                                  style={{
                                    padding: '16px 32px',
                                    background: isProcessing 
                                      ? 'rgba(203, 213, 224, 0.3)'
                                      : 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '12px',
                                    fontSize: '17px',
                                    fontWeight: '700',
                                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                                    boxShadow: isProcessing ? 'none' : '0 6px 16px rgba(72, 187, 120, 0.5)',
                                    transition: 'all 0.3s ease'
                                  }}
                                >
                                  ▶️ Next Section
                                </button>
                              )}
                              <button
                                onClick={() => explainCurrentSection()}
                                disabled={isProcessing}
                                style={{
                                  padding: '16px 32px',
                                  background: isProcessing
                                    ? 'rgba(203, 213, 224, 0.3)'
                                    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '12px',
                                  fontSize: '17px',
                                  fontWeight: '700',
                                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                                  boxShadow: isProcessing ? 'none' : '0 6px 16px rgba(245, 158, 11, 0.5)',
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                🔄 Repeat Section
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Story Section - Only show for Section 1 */}
                  {currentSection === 0 && (
                    <div style={{
                      background: 'rgba(255,215,0,0.1)',
                      border: '2px solid rgba(255,215,0,0.3)',
                      borderRadius: '12px',
                      padding: '20px',
                      marginBottom: '20px'
                    }}>
                      <h3 style={{ color: '#ffd700', fontSize: '22px', marginBottom: '12px' }}>
                        📖 {lessonContent.title}
                      </h3>
                      {lessonContent.story && (
                        <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '15px', lineHeight: '1.7' }}>
                          {lessonContent.story}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Learning Objectives */}
                  {lessonContent.learning_objectives && lessonContent.learning_objectives.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ color: '#00d9ff', fontSize: '18px', marginBottom: '10px' }}>
                        🎯 Learning Objectives:
                      </h4>
                      <ul style={{
                        color: 'rgba(255,255,255,0.9)',
                        fontSize: '14px',
                        lineHeight: '1.8',
                        paddingLeft: '20px'
                      }}>
                        {lessonContent.learning_objectives.map((obj, idx) => (
                          <li key={idx}>{obj}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Content Sections */}
                  {lessonContent.sections && lessonContent.sections.length > 0 && (
                    <div>

                      {/* Current Section Content */}
                      <div style={{
                        background: 'rgba(0,0,0,0.3)',
                        borderRadius: '12px',
                        padding: '25px',
                        marginBottom: '20px'
                      }}>
                        <h4 style={{
                          color: '#00d9ff',
                          fontSize: '22px',
                          marginBottom: '20px',
                          textAlign: 'center',
                          fontWeight: '700'
                        }}>
                          📝 {lessonContent.sections[currentSection].title}
                        </h4>
                        <div 
                          style={{
                            color: 'rgba(255,255,255,0.95)',
                            fontSize: '15px',
                            lineHeight: '1.9',
                            whiteSpace: 'pre-wrap'
                          }}
                          dangerouslySetInnerHTML={{ 
                            __html: lessonContent.sections[currentSection].content
                              .replace(/\*\*(.*?)\*\*/g, '<strong style="color: #ffd700;">$1</strong>')
                              .replace(/\n/g, '<br/>')
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Key Concepts */}
                  {lessonContent.key_concepts && Object.keys(lessonContent.key_concepts).length > 0 && (
                    <div style={{
                      background: 'rgba(0,217,255,0.1)',
                      border: '2px solid rgba(0,217,255,0.3)',
                      borderRadius: '12px',
                      padding: '20px'
                    }}>
                      <h4 style={{ color: '#00d9ff', fontSize: '18px', marginBottom: '12px' }}>
                        🔑 Key Concepts:
                      </h4>
                      {Object.entries(lessonContent.key_concepts).map(([term, definition], idx) => (
                        <div key={idx} style={{ marginBottom: '12px' }}>
                          <strong style={{ color: '#ffd700', fontSize: '14px' }}>{term}:</strong>
                          <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', marginLeft: '6px' }}>
                            {definition}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', padding: '40px' }}>
                  No lesson content available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <audio
        ref={audioRef}
        onEnded={handleAudioEnd}
        style={{ display: 'none' }}
      />

      <style>{`
        @keyframes floatGenie {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-15px) rotate(1deg);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 0.4;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }

        @keyframes floatKids {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg);
            opacity: 0.8;
          }
          50% { 
            transform: translateY(-25px) rotate(15deg);
            opacity: 1;
          }
        }

        @keyframes twinkle {
          0%, 100% {
            opacity: 0;
            transform: scale(0);
          }
          50% {
            opacity: 1;
            transform: scale(1);
          }
        }

        ${[...Array(8)].map((_, i) => `
          @keyframes orbit${i} {
            from {
              transform: rotate(${i * 45}deg) translateX(150px) rotate(-${i * 45}deg);
            }
            to {
              transform: rotate(${i * 45 + 360}deg) translateX(150px) rotate(-${i * 45 + 360}deg);
            }
          }
        `).join('\n')}
      `}</style>
    </div>
  );
};

// Wrap the lesson with quiz functionality
const CodingLesson6 = () => {
  return (
    <LessonWrapper
      courseType="coding"
      lessonNumber={6}
      pdfUrl="https://customer-assets.emergentagent.com/job_920c0cc1-93ee-4984-b68a-08d1f64e9970/artifacts/s2n2bqfi_Kids%20Genie%20Coding%20Lesson%206.pdf"
      pdfTitle="Coding Lesson 6 Student Book"
    >
      <CodingLesson6Content />
    </LessonWrapper>
  );
};

export default CodingLesson6;
